import { HttpError, NetworkError, ResponseParseError } from '@luminovo/api-contracts';

/** A connection that never carried the request says nothing about it, and browsers recover from flaky wifi. */
const NETWORK_ERROR_ATTEMPTS = 6;

/** TanStack Query's default, for the genuinely transient: a server that failed once may answer next time. */
const DEFAULT_ATTEMPTS = 3;

// 408: the server gave up waiting (~200 a fortnight on `GET /activities/:activityId`); the next attempt can win.
// 401: each attempt reads the token afresh; retries let it renew before `ErrorFallback` sends the user to login.
// Since a renewal stopped remounting the pages (SOU-5207), 401 retries wait out their delay instead of being cancelled.
// No 429: none reaches `http()`, and a fixed-backoff retry makes a rate limit worse; honour `Retry-After` if one does.
/** The only 4xx that aren't deterministic, so the only ones worth repeating. */
const RETRYABLE_CLIENT_ERROR_STATUSES = new Set([401, 408]);

/** Repeats what might come back differently; repeating a 4xx, the server's verdict, only delayed the UI. */
export function shouldRetryRequest(failureCount: number, error: unknown): boolean {
    // The same body fails to parse again, after the server has computed it again (24 GB for GPV Nego's 20 scenarios).
    if (error instanceof ResponseParseError) {
        return false;
    }

    if (error instanceof NetworkError) {
        return failureCount < NETWORK_ERROR_ATTEMPTS;
    }

    if (
        error instanceof HttpError &&
        isClientError(error.status) &&
        !RETRYABLE_CLIENT_ERROR_STATUSES.has(error.status)
    ) {
        return false;
    }

    // 5xx, and non-HTTP failures such as a failed schema check, where there is no status to reason about.
    return failureCount < DEFAULT_ATTEMPTS;
}

function isClientError(status: number): boolean {
    return status >= 400 && status < 500;
}
